/* eslint-disable import/no-default-export */
import React, { useState, useEffect } from 'react';
import { AutomaticMigrationConfirmation } from '../AutomaticOnboarding/AtlassianAutomaticOnboardingOverlays/AutomaticMigrationConfirmation';
import { AtlassianProfile } from '../AutomaticOnboarding/AtlassianAutomaticOnboardingOverlays/AtlassianProfile';
import { UpdateAccount } from '../AutomaticOnboarding/AtlassianAutomaticOnboardingOverlays/UpdateAccount';
import { EmailMismatch } from '../AutomaticOnboarding/AtlassianAutomaticOnboardingOverlays/EmailMismatch';
import { useDismissAtlassianAccountRelinkedConfirmationMutation } from './DismissAtlassianAccountRelinkedConfirmationMutation.generated';
import { usePrepMemberForAtlassianAccountOnboardingMutation } from '../PrepMemberForAtlassianAccountOnboardingMutation.generated';
import { Analytics } from '@trello/atlassian-analytics';
import { AtlassianConfirmation } from '../v2Overlays/AtlassianConfirmation';
import api from 'app/gamma/src/api';
import { sendCrashEvent } from '@trello/error-reporting';
import { avatarsFromAvatarUrl } from '@trello/members';
import { needsAtlassianAccountRelinkedConfirmation } from './needsAtlassianAccountRelinkedConfirmation';

const analyticsSource = 'atlassianAccountAutomaticOnboardingOverlay';

export enum ScreenType {
  ATLASSIAN_PROFILE = 'atlassianAutomaticOnboardingProfileModal',
  AUTOMATIC_MIGRATION_CONFIRMATION = 'atlassianAutomaticOnboardingConfirmationModal',
  UPDATE_ACCOUNT = 'atlassianAutomaticOnboardingUpdateAccountModal',
  EMAIL_MISMATCH = 'atlassianAutomaticOnboardingEmailMismatchModal',
  ACCOUNT_CONFIRMATION = 'atlassianAccountConfirmationModal',
}

export interface Login {
  id: string;
  email: string;
}
interface AtlassianAccountOnboardingOverlayProps {
  analyticsContext: object;
  analyticsContainers: object;
  credentialsRemovedCount?: string | null;
  id: string;
  name: string;
  screen: ScreenType | undefined;
  aaEmail: string;
  logins: Login[];
  oneTimeMessagesDismissed: string[];
  orgName?: string;
  dismiss: () => void;
  setScreen: (screenType: ScreenType) => void;
}

interface AtlassianProfileResponse {
  publicName: string;
  email: string;
  avatarUrl: string;
}

export const AtlassianAutomaticOnboardingOverlay: React.FunctionComponent<AtlassianAccountOnboardingOverlayProps> = ({
  analyticsContext,
  analyticsContainers,
  credentialsRemovedCount,
  id,
  name,
  screen,
  aaEmail,
  logins,
  oneTimeMessagesDismissed,
  orgName,
  dismiss,
  setScreen,
}) => {
  const [
    dismissAaRelinkedConfirmation,
  ] = useDismissAtlassianAccountRelinkedConfirmationMutation();
  const [removeEmails] = usePrepMemberForAtlassianAccountOnboardingMutation();
  const [atlassianProfileName, setAtlassianProfileName] = useState('');
  const [atlassianProfileEmail, setAtlassianProfileEmail] = useState('');
  const [avatars, setAvatars] = useState({});

  const getAtlassianProfile = async () => {
    try {
      const response = await api.client.rest.get<AtlassianProfileResponse>(
        `members/me/aaProfile`,
      );
      const { avatarUrl, publicName, email } = response;
      const atlassianAvatars =
        avatarUrl && avatarsFromAvatarUrl(avatarUrl, true);
      setAvatars(atlassianAvatars);
      setAtlassianProfileName(publicName);
      setAtlassianProfileEmail(email);
    } catch (error) {
      sendCrashEvent(error);
    }
  };

  useEffect(() => {
    getAtlassianProfile();
  }, []);

  const aaLogin = logins?.find(({ email }) => email === aaEmail);
  const aaLoginId = aaLogin && aaLogin.id;
  const otherLogins = logins?.filter(({ email }) => email !== aaEmail);
  const nonAaLoginIds = otherLogins?.map((login) => login.id);

  orgName = orgName as string;

  const overlayAnalyticsContext = {
    ...analyticsContext,
    screen,
  };

  switch (screen) {
    case ScreenType.AUTOMATIC_MIGRATION_CONFIRMATION:
      return (
        <AutomaticMigrationConfirmation
          aaEmail={atlassianProfileEmail}
          name={atlassianProfileName}
          avatars={avatars}
          onDismiss={dismiss}
          orgName={orgName}
          onTrackScreenEvent={() => {
            Analytics.sendScreenEvent({
              name: screen,
              attributes: analyticsContext,
              containers: analyticsContainers,
            });
          }}
          onClickOk={() => {
            Analytics.sendClickedButtonEvent({
              attributes: overlayAnalyticsContext,
              source: analyticsSource,
              buttonName: 'atlassianAutomaticOnboardingCompleteButton',
            });
            dismiss();
          }}
        />
      );
    case ScreenType.ATLASSIAN_PROFILE:
      return (
        <AtlassianProfile
          aaEmail={atlassianProfileEmail}
          name={atlassianProfileName}
          avatars={avatars}
          otherLogins={otherLogins}
          onDismiss={dismiss}
          orgName={orgName}
          credentialsRemovedCount={credentialsRemovedCount}
          onTrackScreenEvent={() => {
            Analytics.sendScreenEvent({
              name: screen,
              attributes: analyticsContext,
              containers: analyticsContainers,
            });
          }}
          onClickOk={async () => {
            const mutations = [];
            aaLoginId &&
              mutations.push(
                removeEmails({
                  variables: {
                    memberId: 'me',
                    aaLoginId,
                    nonAaLoginIds,
                  },
                }),
              );
            needsAtlassianAccountRelinkedConfirmation(
              oneTimeMessagesDismissed,
            ) &&
              mutations.push(
                dismissAaRelinkedConfirmation({
                  variables: { messageId: 'aa-relinked-confirmation' },
                }),
              );
            await Promise.all(mutations);
            Analytics.sendClickedButtonEvent({
              attributes: overlayAnalyticsContext,
              containers: analyticsContainers,
              source: analyticsSource,
              buttonName:
                'atlassianAutomaticOnboardingContinueToAtlassianButton',
            });
          }}
        />
      );
    case ScreenType.UPDATE_ACCOUNT:
      return (
        <UpdateAccount
          aaEmail={aaEmail}
          orgName={orgName}
          onDismiss={dismiss}
          onClickUpdateAccount={() => {
            Analytics.sendClickedButtonEvent({
              attributes: overlayAnalyticsContext,
              containers: analyticsContainers,
              source: analyticsSource,
              buttonName: 'atlassianAutomaticOnboardingUpdateAccountButton',
            });
            setScreen(ScreenType.ATLASSIAN_PROFILE);
          }}
        />
      );
    case ScreenType.EMAIL_MISMATCH:
      return (
        <EmailMismatch
          aaEmail={aaEmail}
          orgName={orgName}
          onDismiss={dismiss}
          onTrackScreenEvent={() => {
            Analytics.sendScreenEvent({
              name: screen,
              attributes: analyticsContext,
              containers: analyticsContainers,
            });
          }}
          onClickLogin={() => {
            Analytics.sendClickedButtonEvent({
              attributes: overlayAnalyticsContext,
              containers: analyticsContainers,
              source: analyticsSource,
              buttonName: 'atlassianAutomaticOnboardingMismatchLoginButton',
            });
            setScreen(ScreenType.EMAIL_MISMATCH);
          }}
        />
      );
    case ScreenType.ACCOUNT_CONFIRMATION:
      return (
        <AtlassianConfirmation
          id={id}
          name={name}
          aaEmail={aaEmail}
          onDismiss={dismiss}
          orgName={orgName}
          onClickOk={() => {
            Analytics.sendClickedButtonEvent({
              attributes: overlayAnalyticsContext,
              containers: analyticsContainers,
              source: analyticsSource,
              buttonName: 'atlassianAutomaticOnboardingConfirmationButton',
            });
            dismiss();
          }}
        />
      );
    default:
      return null;
  }
};
